import { useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import style from "../../../components/ApplicationAccess/ApplicationAccess.module.css";
import ApplicationAccessToggle from "../../../components/ApplicationAccess/ApplicationAccessToggle";
import { formatBlockDate } from "../../../components/ApplicationAccess/blockDates";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";
import Paginator from "../../../components/CommonComponents/Paginator/Paginator";
import Table, {
  tableCustomStyles,
} from "../../../components/CommonComponents/Table/Table";
import PageHeader from "../../../components/pageHeader/pageHeader";
import {
  TAccessBlock,
  TAccessScope,
  TAccessUser,
  TAccessUserType,
  useAccessUserTypes,
  useAccessUsers,
} from "../../../hooks/application-access.hooks";
import { useDebounce } from "../../../hooks/common";

/** What the block columns need to know about a row, whatever the row is. */
type TBlockTarget = {
  scope: TAccessScope;
  target: string;
  label: string;
  block: TAccessBlock | null;
};

/**
 * The Status, Blocked Until, Reason and Block columns, shared by the user
 * type table and the user table. `toTarget` says what a row of that table
 * is blocked as.
 */
function buildBlockColumns<Row>(
  toTarget: (row: Row) => TBlockTarget
): TableColumn<Row>[] {
  return [
    {
      name: "Status",
      cell: (row) => {
        const { block } = toTarget(row);
        return (
          <button className={block ? "inactiveButtonCss" : "activeButtonCss"}>
            {block ? "Blocked" : "Allowed"}
          </button>
        );
      },
    },
    {
      name: "Blocked Until",
      cell: (row) => {
        const { block } = toTarget(row);
        if (!block) return "-";
        return block.until ? formatBlockDate(block.until) : "Until switched off";
      },
    },
    {
      name: "Reason",
      wrap: true,
      cell: (row) => toTarget(row).block?.reason || "-",
    },
    {
      name: "Block",
      cell: (row) => {
        const { scope, target, label, block } = toTarget(row);
        return (
          <ApplicationAccessToggle
            scope={scope}
            target={target}
            targetLabel={label}
            block={block}
          />
        );
      },
    },
  ];
}

const userTypeColumns: TableColumn<TAccessUserType>[] = [
  { name: "User Type", selector: (row) => row.userType },
  ...buildBlockColumns<TAccessUserType>((row) => ({
    scope: "user_type",
    target: row.userType,
    label: `User type: ${row.userType}`,
    block: row.block,
  })),
];

const userColumns: TableColumn<TAccessUser>[] = [
  { name: "Name", selector: (row) => row.name, wrap: true },
  { name: "User Type", selector: (row) => row.userType },
  { name: "Mobile No", selector: (row) => row.mobileNumber },
  ...buildBlockColumns<TAccessUser>((row) => ({
    scope: "user",
    target: String(row.userId),
    label: `${row.name} (${row.userType})`,
    block: row.block,
  })),
];

/**
 * Admin screen of the Application Access feature: bar a whole user type, or
 * one user, from starting and submitting applications. The screen only lays
 * out the two tables; placing and lifting a block is done by
 * ApplicationAccessToggle.
 */
export default function ApplicationAccess() {
  const [searchQuery, setSearchQuery] = useState("");
  const [userType, setUserType] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(searchQuery);

  const { data: userTypes = [], isLoading: loadingUserTypes } = useAccessUserTypes();
  const { data: usersPage, isLoading: loadingUsers } = useAccessUsers({
    search: debouncedSearch,
    userType,
    page,
  });

  /** A new search or filter starts again from the first page. */
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, userType]);

  return (
    <div>
      <div className={style.bodyBox}>
        <PageHeader
          title="Application Access"
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showSearchButton={true}
          showAdditionalButton={false}
          buttonTitle=""
          showGridButton={false}
          showFilterButton={false}
        />

        <p className={style.sectionTitle}>Block by User Type</p>
        <BoxComponent>
          <Table
            columns={userTypeColumns as TableColumn<unknown>[]}
            data={userTypes}
            customStyles={tableCustomStyles}
            progressPending={loadingUserTypes}
            isPagination={false}
          />
        </BoxComponent>

        <p className={`${style.sectionTitle} ${style.sectionGap}`}>
          Block by User
        </p>
        <BoxComponent>
          <div className={style.filterRow}>
            <select
              className={style.selectBox}
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
            >
              <option value="">All User Types</option>
              {userTypes.map((row) => (
                <option key={row.userType} value={row.userType}>
                  {row.userType}
                </option>
              ))}
            </select>
          </div>
          <Table
            columns={userColumns as TableColumn<unknown>[]}
            data={usersPage?.users ?? []}
            customStyles={tableCustomStyles}
            progressPending={loadingUsers}
            isPagination={false}
          />
          <Paginator
            limit={usersPage?.pageSize ?? 10}
            page={page}
            setPage={setPage}
            count={usersPage?.total ?? 0}
          />
        </BoxComponent>
      </div>
    </div>
  );
}
